import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import imageCompression from 'browser-image-compression';

import { API_URL } from '../config/api';
import { useRole } from '../context/RoleContext';
import { useAlert } from '../context/AlertContext';
import useModalScrollLock from '../hooks/useModalScrollLock';
import { validatePhoneForSubmission, cleanPhoneForDatabase, validateEmailForSubmission } from '../utils/phoneFormatter';

import ImmigrantForm from '../components/ImmigrantForm';
import RecordDetailsModal from '../components/RecordDetailsModal.jsx';
import DetentionForm from '../components/DetentionForm';
import StandaloneDetentionModal from '../components/StandaloneDetentionModal';
import DetentionMonthlySummary from '../components/DetentionMonthlySummary';
import DetentionEditModal from '../components/DetentionEditModal';
import UserManagement from '../components/UserManagement';
import AuditDashboard from '../components/AuditDashboard';

import '../Style/App.css';

export default function Dashboard() {
  const token = localStorage.getItem('idToken');
  const nav = useNavigate();
  const { userRole, isSupervisor, isAdmin, isSecurityAdmin, isDetention, canAccessDetention, canDeleteRecords, clearUserRole } = useRole();
  const { showSuccess, showError, showConfirm } = useAlert();

  // Additional role-based access control
  const hasDetentionAccess = isDetention || isAdmin || isSecurityAdmin;

  // Function to get user email from token (memoized to prevent infinite loops)
  const getUserEmailFromToken = useCallback(() => {
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.email || '';
      } catch (error) {
        console.error('Error decoding token for email:', error);
        return '';
      }
    }
    return '';
  }, [token]);

  // Function to format phone number for display
  const formatPhoneForDisplay = (phoneNumber) => {
    if (!phoneNumber) return '';
    // Remove all non-digits
    const cleaned = phoneNumber.replace(/\D/g, '');
    // Format as XXX-XXX-XXXX if 10 digits
    if (cleaned.length === 10) {
      return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phoneNumber; // Return as-is if not 10 digits
  };

  // Function to fetch and prefill user profile data
  const prefillUserData = useCallback(async () => {
    if (!token) return;

    try {
      const response = await axios.get(`${API_URL}/user/profile`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data) {
        const userData = response.data;

        const updatedData = {
          officerFirstName: userData.firstName || '',
          officerLastName: userData.lastName || '',
          officerEmail: userData.email || '',
          officerPhone: formatPhoneForDisplay(userData.phoneNumber) || '',
          badgeOrId: userData.badgeId || '',
          officerTitle: userData.title || ''
        };

        setFormData(prev => ({
          ...prev,
          ...updatedData
        }));
      }
    } catch (error) {
      console.error('Error fetching user profile for prefill:', error);
      // Fallback to just email from token
      const userEmail = getUserEmailFromToken();
      if (userEmail) {
        setFormData(prev => ({
          ...prev,
          officerEmail: userEmail
        }));
      }
    }
  }, [token]);

  // Function to get user profile data for prefilling (memoized to prevent infinite loops)
  const getUserProfileData = useCallback(async () => {
    if (!token) return null;

    try {
      const response = await axios.get(`${API_URL}/user/profile`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data) {
        const userData = response.data;

        return {
          officerFirstName: userData.firstName || '',
          officerLastName: userData.lastName || '',
          officerEmail: userData.email || '',
          officerPhone: formatPhoneForDisplay(userData.phoneNumber) || '',
          badgeOrId: userData.badgeId || '',
          officerTitle: userData.title || ''
        };
      }
    } catch (error) {
      console.error('Error fetching user profile for prefill:', error);
      // Return fallback data
      return {
        officerFirstName: '',
        officerLastName: '',
        officerEmail: getUserEmailFromToken() || '',
        officerPhone: '',
        badgeOrId: '',
        officerTitle: ''
      };
    }

    return null;
  }, [token, getUserEmailFromToken]);

  // Auto-populate officer information when token changes or component mounts
  useEffect(() => {
    if (token) {
      prefillUserData();
    }
  }, [token, prefillUserData]);

  // Check if token exists and is not expired
  useEffect(() => {
    if (token) {
      try {
        // Decode the token to check expiration (without verification)
        const payload = JSON.parse(atob(token.split('.')[1]));
        const currentTime = Math.floor(Date.now() / 1000);

        if (payload.exp < currentTime) {
          // Token has expired, logging out user
          // Clear all tokens
          localStorage.removeItem('idToken');
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          clearUserRole();
          showError('Your session has expired. Please log in again.');
          nav('/login');
          return;
        }


      } catch (error) {
        console.error('Error decoding token:', error);
        localStorage.removeItem('idToken');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        clearUserRole();
        nav('/login');
      }
    }
  }, [token, nav, clearUserRole, showError]);

  // Set up periodic token expiration check
  useEffect(() => {
    if (!token) return;

    // Check token expiration every 30 seconds
    const interval = setInterval(() => {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const currentTime = Math.floor(Date.now() / 1000);

        if (payload.exp < currentTime) {
          // Token expired during periodic check, logging out user
          // Clear all tokens
          localStorage.removeItem('idToken');
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          clearUserRole();
          showError('Your session has expired. Please log in again.');
          nav('/login');
        }
      } catch (error) {
        console.error('Error during periodic token check:', error);
        // If we can't decode the token, it's invalid
        localStorage.removeItem('idToken');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        clearUserRole();
        nav('/login');
      }
    }, 30000); // Check every 30 seconds

    // Cleanup interval on component unmount
    return () => clearInterval(interval);
  }, [token, nav, clearUserRole, showError]);

  // Helper function to format dates - fixed to handle timezone issues
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';

    // If the date string is in YYYY-MM-DD format, treat it as a local date
    if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateString.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }

    // For other date formats, use the original logic
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // State for form data
  const [formData, setFormData] = useState({
    // 1. Date of Contact
    contactDate: '',

    // 2. Contacting Agency - User must enter manually
    ori: '',
    agencyName: '',
    is_submitter_dio: false, // New field for DIO status
    is_non_criminal_arrest: false, // New field for non-criminal arrest (controls TFM visibility)
    officerFirstName: '',
    officerLastName: '',
    badgeOrId: '',
    officerTitle: '',
    officerPhone: '',
    officerEmail: getUserEmailFromToken(), // Auto-populate with logged-in user's email

    // 3-5. Location of Contact
    contactCounty: '',
    contactStreetAddress: '',
    contactApartmentUnit: '',
    contactCity: '',

    // 6. Illegal Alien's Personal Information
    firstName: '',
    middleName: '',
    lastName: '',
    countryOfCitizenship: '',
    hasDualCitizenship: false,
    dualCitizenshipCountry: '',
    alienRegistrationNumber: '',
    dateOfBirth: '',
    sex: '',

    // 7. Illegal Alien's Current Address
    currentStreetAddress: '',
    currentCity: '',
    currentState: '',
    currentZipCode: '',
    phoneNumber: '',

    // 8. Illegal Alien's Current Employer
    employerName: '',
    employerStreetAddress: '',
    employerCity: '',
    employerState: '',
    employerZipCode: '',
    employerPhoneNumber: '',

    // 9. Gang Affiliations
    hasGangAffiliations: undefined, // Force user to select
    gangName: '',

    // 9.5. Contact Description
    contactDescription: '',

    // 9.6. Federal Agency Contact
    federalAgencyContact: '',

    // 9.7. Collaboration Agency
    collaborationAgency: '',

    // 9.8. Criminal History (required)
    hasCriminalHistory: undefined, // Force user to select

    // 9.9. Arrest Information (required)
    wasArrested: undefined, // Force user to select

    // 9.10. 287(g) Authority Usage (conditional - only if arrested)
    used287gAuthority: undefined, // Force user to select if arrested

    // 10. Vehicle Information
    vehicleYear: '',
    vehicleMake: '',
    vehicleModel: '',
    vehicleTag: '',
    vehicleTagState: '',

    // 12. Citizenship Status Verification
    citizenshipVerifiedWithIce: false,
    iceCallCenterContactName: '',
    iceCallCenterContactId: '',

    // 13. Arrest Information
    arrestCharges: '',
    iceContacted: false,

    // TFM Arrest Sheet Fields (conditionally shown when is_submitter_dio = true)
    tfmLawEnforcementAgency: '',
    tfmTaskForceOfficerName: '',
    tfmBadgeNumber: '',
    tfmIceCredentialNumber: '',
    tfmCellPhone: '',
    tfmArrestDate: '',
    tfmArrestTime: '',
    tfmEventNumber: '',
    tfmIceApprovalOfficerName: '',
    tfmIceApprovalDatetime: '',
    tfmArrestLocation: '',
    tfmConsentGrantedBy: '',
    tfmConsentTime: '',
    tfmSearchCompletedTime: '',
    tfmInvestigationHours: '',
    tfmNotes: '',
    // TFM Alien Information
    tfmAlienName: '',
    tfmAlienDob: '',
    tfmAlienANumber: '',
    tfmAlienSex: '',
    tfmAlienNationality: '',
    tfmAlienFbiNumber: '',
    iceResponded: false,
    detainedByIce: false,
    notDetainedReason: '',
    iceNoResponseReason: '',

    // 14. Photo
    photoUrl: '',
    photoFile: null
  });

  // State for search functionality
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage] = useState(10);
  const [records, setRecords] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // State for approved records
  const [approvedRecords, setApprovedRecords] = useState([]);
  const [approvedCurrentPage, setApprovedCurrentPage] = useState(1);
  const [approvedTotalPages, setApprovedTotalPages] = useState(0);
  const [loadingApproved, setLoadingApproved] = useState(false);

  // State for detention record list (separate from approved records)
  const [detentionListRecords, setDetentionListRecords] = useState([]);
  const [detentionListCurrentPage, setDetentionListCurrentPage] = useState(1);
  const [detentionListTotalPages, setDetentionListTotalPages] = useState(0);
  const [detentionListSearchTerm, setDetentionListSearchTerm] = useState('');
  const [loadingDetentionList, setLoadingDetentionList] = useState(false);
  const [detentionListRefreshTrigger, setDetentionListRefreshTrigger] = useState(0);

  // State for all records (officer view)
  const [allRecords, setAllRecords] = useState([]);
  const [allCurrentPage, setAllCurrentPage] = useState(1);
  const [allTotalPages, setAllTotalPages] = useState(0);
  const [allTotalCount, setAllTotalCount] = useState(0);
  const [allSearchTerm, setAllSearchTerm] = useState('');
  const [loadingAll, setLoadingAll] = useState(false);

  // Tab management state - default to add-record for all users
  const [activeTab, setActiveTab] = useState('add-record');
  const [approvedSearchTerm, setApprovedSearchTerm] = useState('');

  // State for record details modal
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [viewContext, setViewContext] = useState(null);



  // State for detention record modal
  const [detentionModalVisible, setDetentionModalVisible] = useState(false);
  const [selectedImmigrantForDetention, setSelectedImmigrantForDetention] = useState(null);

  // State for detention records functionality
  const [detentionRecords, setDetentionRecords] = useState([]);
  const [detentionLoading, setDetentionLoading] = useState(false);
  const [detentionCurrentPage, setDetentionCurrentPage] = useState(1);
  const [detentionTotalPages, setDetentionTotalPages] = useState(1);
  const [detentionTotalCount, setDetentionTotalCount] = useState(0);
  const [detentionFilters, setDetentionFilters] = useState({
    month: '',
    facilityType: '',
    releasedMonth: '',
    arrestingAgency: '',
    iceCustodyPending: false,
    releasedHousingCostsUpdates: false,
    housingCostReimbursementReceived: false
  });
  const [availableArrestingAgencies, setAvailableArrestingAgencies] = useState([]);
  const [detentionSearchTerm, setDetentionSearchTerm] = useState('');
  const [detentionRecordModalVisible, setDetentionRecordModalVisible] = useState(false);
  const [selectedDetentionRecord, setSelectedDetentionRecord] = useState(null);
  const [editDetentionModalVisible, setEditDetentionModalVisible] = useState(false);
  const [selectedDetentionRecordForEdit, setSelectedDetentionRecordForEdit] = useState(null);
  const [standaloneDetentionModalVisible, setStandaloneDetentionModalVisible] = useState(false);

  // Prevent body scrolling when any modal is open
  const anyModalOpen = modalVisible || detentionModalVisible ||
                       detentionRecordModalVisible || editDetentionModalVisible ||
                       standaloneDetentionModalVisible;
  useModalScrollLock(anyModalOpen);

  // Ref for photo file input to clear it after submission
  const photoFileInputRef = useRef(null);

  // Ensure officers can only access the add-record and all-records tabs
  useEffect(() => {
    if (userRole === 'Officer' && (activeTab === 'pending-approval' || activeTab === 'approved-records' || activeTab === 'user-management' || activeTab === 'audit-dashboard')) {
      setActiveTab('add-record');
    }
    // Ensure only admins and security admins can access user management
    if (userRole !== 'Admin' && userRole !== 'Security Admin' && activeTab === 'user-management') {
      setActiveTab('add-record');
    }
    // Ensure non-security-admins can't access audit dashboard
    if (!isSecurityAdmin && activeTab === 'audit-dashboard') {
      setActiveTab('add-record');
    }
    // Ensure Detention users can only access detention-related tabs
    const allowedDetentionTabs = ['add-detention-record', 'view-detention-records', 'monthly-summary'];
    if (userRole === 'Detention' && !allowedDetentionTabs.includes(activeTab)) {
      setActiveTab('add-detention-record'); // Default to add detention record for detention users
    }
  }, [userRole, activeTab, isSupervisor, isAdmin, isSecurityAdmin, isDetention, canAccessDetention, hasDetentionAccess]);



  // Set up authorization header
  const authHeader = useMemo(() => ({
    headers: {
      Authorization: token ? `Bearer ${token}` : ''
    }
  }), [token]);

  // Handle logout
  const handleLogout = () => {
    // Clear all tokens
    localStorage.removeItem('idToken');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    clearUserRole();
    nav('/login');
  };

  // Auto-populate form defaults removed - users must enter ORI and Agency Name manually

  // Ensure officer fields are always populated when on add-record tab
  useEffect(() => {
    const ensureOfficerFieldsPopulated = async () => {
      // Only run when on add-record tab and not editing
      if (activeTab !== 'add-record') {
        return;
      }

      try {
        // Fetch user profile data only (no agency defaults)
        const userProfileData = await getUserProfileData();

        // Check if any required fields are empty and populate them
        setFormData(prevData => {
          const needsOfficerUpdate = !prevData.officerFirstName || !prevData.officerLastName ||
                                   !prevData.badgeOrId || !prevData.officerTitle ||
                                   !prevData.officerPhone || !prevData.officerEmail;

          if (needsOfficerUpdate) {
            const updatedData = {
              ...prevData,
              // Officer information from user profile (no agency auto-population)
              officerFirstName: prevData.officerFirstName || userProfileData?.officerFirstName || '',
              officerLastName: prevData.officerLastName || userProfileData?.officerLastName || '',
              badgeOrId: prevData.badgeOrId || userProfileData?.badgeOrId || '',
              officerTitle: prevData.officerTitle || userProfileData?.officerTitle || '',
              officerPhone: prevData.officerPhone || userProfileData?.officerPhone || '',
              officerEmail: prevData.officerEmail || userProfileData?.officerEmail || getUserEmailFromToken()
            };

            return updatedData;
          }

          return prevData;
        });
      } catch (error) {
        // Silently handle error - still ensure officer email is set
        setFormData(prevData => {
          const needsUpdate = !prevData.officerEmail;

          if (needsUpdate) {
            return {
              ...prevData,
              officerEmail: prevData.officerEmail || getUserEmailFromToken()
            };
          }

          return prevData;
        });
      }
    };

    ensureOfficerFieldsPopulated();
  }, [activeTab]); // Only depend on activeTab to prevent infinite loops

  // Fetch unapproved records from API
  useEffect(() => {
    // Don't fetch if no token is available or not on the pending approval tab
    if (!token || activeTab !== 'pending-approval') {
      return;
    }

    const fetchRecords = async () => {

      setLoading(true);
      setError(null);

      try {
        const apiUrl = process.env.NODE_ENV === 'production'
          ? `/api/immigrants?page=${currentPage}&limit=${recordsPerPage}&search=${searchTerm}&approved=false&exclude_with_detention=true`
          : `http://localhost:8080/api/immigrants?page=${currentPage}&limit=${recordsPerPage}&search=${searchTerm}&approved=false&exclude_with_detention=true`;

        const response = await axios.get(apiUrl, authHeader);



        // Check if response has the expected structure
        if (!response.data || !response.data.data) {
          console.error('Invalid response structure:', response.data);
          setError('Invalid response from server. Please try again.');
          return;
        }

        // Transform data to match our frontend format
        const transformedRecords = response.data.data.map(record => {

          return {
            id: record.id,
            contactDate: record.contact_date,
            agencyName: record.agency_name,
            firstName: record.first_name,
            lastName: record.last_name,
            countryOfCitizenship: record.country_of_citizenship,
            sex: record.sex,
            is_approved: record.is_approved,
            // Store the original record ID to ensure we have the correct ID
            originalId: record.id
          };
        });


        // Sort records by ID to ensure lowest ID numbers appear first
        const sortedRecords = transformedRecords.sort((a, b) => a.id - b.id);
        setRecords(sortedRecords);

        // Safely access pagination data with fallbacks
        const pagination = response.data.pagination || {};
        setTotalPages(pagination.totalPages || 1);
        setTotalCount(pagination.totalCount || 0);
      } catch (err) {
        console.error('Error fetching unapproved records:', err);
        // Error details logged
        setError('Failed to fetch records. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchRecords();
  }, [token, currentPage, recordsPerPage, searchTerm, refreshTrigger, authHeader, activeTab]);

  // Fetch approved records from API
  useEffect(() => {
    // Don't fetch if no token is available or not on the approved records tab
    if (!token || activeTab !== 'approved-records') {
      return;
    }

    const fetchApprovedRecords = async () => {
      setLoadingApproved(true);

      try {
        const apiUrl = process.env.NODE_ENV === 'production'
          ? `/api/immigrants?page=${approvedCurrentPage}&limit=${recordsPerPage}&search=${approvedSearchTerm}&approved=true`
          : `http://localhost:8080/api/immigrants?page=${approvedCurrentPage}&limit=${recordsPerPage}&search=${approvedSearchTerm}&approved=true`;

        const response = await axios.get(apiUrl, authHeader);



        // Check if response has the expected structure
        if (!response.data || !response.data.data) {
          console.error('Invalid response structure for approved records:', response.data);
          return;
        }

        // Transform data to match our frontend format
        const transformedRecords = response.data.data.map(record => {
          return {
            id: record.id,
            contactDate: record.contact_date,
            agencyName: record.agency_name,
            firstName: record.first_name,
            lastName: record.last_name,
            countryOfCitizenship: record.country_of_citizenship,
            sex: record.sex,
            is_approved: record.is_approved,
            hasDetentionRecord: record.has_detention_record,
            originalId: record.id
          };
        });


        // Sort records by ID to ensure lowest ID numbers appear first
        const sortedRecords = transformedRecords.sort((a, b) => a.id - b.id);
        setApprovedRecords(sortedRecords);

        // Safely access pagination data with fallbacks
        const pagination = response.data.pagination || {};
        setApprovedTotalPages(pagination.totalPages || 1);
      } catch (err) {
        console.error('Error fetching approved records:', err);
        // Error details logged
      } finally {
        setLoadingApproved(false);
      }
    };

    fetchApprovedRecords();
  }, [token, approvedCurrentPage, recordsPerPage, approvedSearchTerm, authHeader, activeTab]);

  // Fetch detention list records from API (separate from approved records)
  useEffect(() => {
    // Don't fetch if no token is available or not on the add detention record tab
    if (!token || activeTab !== 'add-detention-record') {
      return;
    }

    const fetchDetentionListRecords = async () => {
      setLoadingDetentionList(true);

      try {
        const apiUrl = process.env.NODE_ENV === 'production'
          ? `/api/immigrants?page=${detentionListCurrentPage}&limit=${recordsPerPage}&search=${detentionListSearchTerm}&approved=true&exclude_with_detention=true`
          : `http://localhost:8080/api/immigrants?page=${detentionListCurrentPage}&limit=${recordsPerPage}&search=${detentionListSearchTerm}&approved=true&exclude_with_detention=true`;

        const response = await axios.get(apiUrl, authHeader);

        // Check if response has the expected structure
        if (!response.data || !response.data.data) {
          console.error('Invalid response structure for detention list records:', response.data);
          return;
        }

        // Transform the data to match expected format
        const transformedRecords = response.data.data.map(record => ({
          id: record.id,
          firstName: record.first_name,
          lastName: record.last_name,
          countryOfCitizenship: record.country_of_citizenship,
          contactDate: record.contact_date,
          hasDetentionRecord: record.has_detention_record,
          // Include all other fields that might be needed
          ...record
        }));

        // Sort records by ID in descending order (highest ID numbers appear first)
        const sortedRecords = transformedRecords.sort((a, b) => b.id - a.id);
        setDetentionListRecords(sortedRecords);

        // Safely access pagination data with fallbacks
        const pagination = response.data.pagination || {};
        setDetentionListTotalPages(pagination.totalPages || 1);
      } catch (err) {
        console.error('Error fetching detention list records:', err);
        // Error details logged
      } finally {
        setLoadingDetentionList(false);
      }
    };

    fetchDetentionListRecords();
  }, [token, detentionListCurrentPage, recordsPerPage, detentionListSearchTerm, authHeader, activeTab, detentionListRefreshTrigger]);

  // Fetch detention records when tab changes or pagination changes - moved after function definition

  // Fetch all records from API (for officer view)
  useEffect(() => {
    // Don't fetch if no token is available, not on all-records tab, or if user doesn't have permission
    if (!token || activeTab !== 'all-records' || (userRole !== 'Officer' && userRole !== 'Admin' && userRole !== 'Security Admin')) {
      return;
    }

    const fetchAllRecords = async () => {
      setLoadingAll(true);

      try {
        // Fetch all records without approval filter
        const apiUrl = process.env.NODE_ENV === 'production'
          ? `/api/immigrants/all?page=${allCurrentPage}&limit=${recordsPerPage}&search=${allSearchTerm}`
          : `http://localhost:8080/api/immigrants/all?page=${allCurrentPage}&limit=${recordsPerPage}&search=${allSearchTerm}`;

        const response = await axios.get(apiUrl, authHeader);



        // Check if response has the expected structure
        if (!response.data || !response.data.data) {
          console.error('Invalid response structure for all records:', response.data);
          return;
        }

        // Transform data to match our frontend format and include DOB
        const transformedRecords = response.data.data.map(record => {
          return {
            id: record.id,
            contactDate: record.contact_date,
            dateOfBirth: record.date_of_birth,
            agencyName: record.agency_name,
            firstName: record.first_name,
            lastName: record.last_name,
            countryOfCitizenship: record.country_of_citizenship,
            sex: record.sex,
            is_approved: record.is_approved,
            originalId: record.id
          };
        });


        // Sort records by ID to ensure lowest ID numbers appear first
        const sortedRecords = transformedRecords.sort((a, b) => a.id - b.id);
        setAllRecords(sortedRecords);

        // Safely access pagination data with fallbacks
        const pagination = response.data.pagination || {};
        setAllTotalPages(pagination.totalPages || 1);
        setAllTotalCount(pagination.totalCount || 0);
      } catch (err) {
        console.error('Error fetching all records:', err);
        // Error details logged
      } finally {
        setLoadingAll(false);
      }
    };

    fetchAllRecords();
  }, [token, allCurrentPage, recordsPerPage, allSearchTerm, authHeader, userRole, activeTab]);





  // Handle delete record from All Records tab
  const handleDeleteAllRecord = async (recordId, recordName) => {
    try {
      const confirmed = await showConfirm(
        'Delete Record',
        `Are you sure you want to permanently delete the record for ${recordName}? This action cannot be undone.`,
        'Delete',
        'Cancel'
      );

      if (!confirmed) return;

      const apiUrl = process.env.NODE_ENV === 'production'
        ? `/api/immigrants/${recordId}`
        : `http://localhost:8080/api/immigrants/${recordId}`;

      await axios.delete(apiUrl, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      showSuccess('Record deleted successfully');

      // Update local state immediately instead of refetching
      setAllRecords(prevRecords => prevRecords.filter(record => record.id !== recordId));

      // Also update other record lists if they contain this record
      setRecords(prevRecords => prevRecords.filter(record => record.id !== recordId));
      setApprovedRecords(prevRecords => prevRecords.filter(record => record.id !== recordId));

    } catch (error) {
      console.error('Error deleting record:', error);
      if (error.response?.status === 403) {
        showError('You do not have permission to delete records.');
      } else if (error.response?.status === 404) {
        showError('Record not found.');
      } else {
        showError('Failed to delete record. Please try again.');
      }
    }
  };

  // Handle file upload for photos
  const handleFileUpload = async (file) => {
    if (!file) return null;

    try {
      // Detailed file analysis - file validation

      // Check file size limit (5MB)
      if (file.size > 5 * 1024 * 1024) {
        showError('File is too large. Please choose an image smaller than 5MB.');
        return null;
      }

      // Check file type
      if (!file.type.startsWith('image/')) {
        showError('Please select an image file (JPG, PNG, GIF, etc.).');
        return null;
      }

      // File validation passed, starting upload

      // Compress image to reduce file size and improve upload reliability
      let fileToUpload = file;
      // Processing image upload

      try {
        const options = {
          maxSizeMB: 1, // Maximum file size in MB
          maxWidthOrHeight: 1920, // Maximum width or height
          useWebWorker: true, // Use web worker for better performance
          fileType: 'image/jpeg', // Convert all images to JPEG for consistency
          quality: 0.8 // JPEG quality (0.1 to 1.0)
        };

        fileToUpload = await imageCompression(file, options);

      } catch (compressionError) {
        console.error('Image compression failed, using original file:', compressionError);
        fileToUpload = file;
      }

      const formData = new FormData();
      formData.append('photo', fileToUpload);

      // Use relative URL for upload in production
      const uploadUrl = process.env.NODE_ENV === 'production' ? '/api/upload-image' : 'http://localhost:8080/api/upload-image';

      const response = await axios.post(uploadUrl, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: token ? `Bearer ${token}` : ''
        },
        timeout: 30000, // Reduce timeout to 30 seconds to match working modal
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          // Upload progress tracking
        }
      });

      // Image upload successful
      // Upload complete
      return response.data.imagePath;
    } catch (error) {
      console.error('=== IMAGE UPLOAD ERROR ===');

      // Safer error logging to prevent issues with malformed error objects
      try {
        // Error details logged for debugging
      } catch (logError) {
        // Error logging failed
      }

      // Provide more specific error messages
      let errorMessage = 'Failed to upload image. ';

      try {
        if (error?.code === 'ERR_NETWORK') {
          errorMessage += `Network connection error. This might be due to file size (${(file.size / 1024 / 1024).toFixed(2)}MB) or connection issues.`;
        } else if (error?.code === 'ECONNABORTED') {
          errorMessage += 'Upload timeout. The file may be too large or the connection is slow.';
        } else if (error?.response?.status === 413) {
          errorMessage += 'File is too large. Please choose a smaller image.';
        } else if (error?.response?.status === 400) {
          errorMessage += 'Invalid file type. Please upload an image file.';
        } else if (error?.response?.status === 500) {
          errorMessage += 'Server error. Please try again or contact support.';
        } else {
          errorMessage += `Please try again. (Error: ${error?.code || 'Unknown'})`;
        }
      } catch (msgError) {
        errorMessage += 'Please try again. (Error details unavailable)';
      }

      showError(errorMessage);
      return null;
    }
  };

  // State for field validation errors
  const [fieldErrors, setFieldErrors] = useState({});

  // Function to highlight invalid fields and scroll to first error
  const highlightInvalidFields = (errors) => {
    const errorFields = {};

    // Define field order as they appear in the form (top to bottom)
    const fieldOrder = [
      'ori', 'agencyName', 'officerFirstName', 'officerLastName', 'badgeOrId',
      'officerTitle', 'officerPhone', 'officerEmail', 'contactCounty',
      'contactStreetAddress', 'contactCity', 'currentState', 'currentZipCode',
      'phoneNumber', 'employerPhoneNumber'
    ];

    let firstErrorField = null;

    // Map validation errors to field names in form order
    errors.forEach(error => {
      if (error.includes('ORI')) {
        errorFields.ori = true;
        if (!firstErrorField) firstErrorField = 'ori';
      }
      if (error.includes('Agency Name')) {
        errorFields.agencyName = true;
        if (!firstErrorField) firstErrorField = 'agencyName';
      }
      if (error.includes('Officer First Name')) {
        errorFields.officerFirstName = true;
        if (!firstErrorField) firstErrorField = 'officerFirstName';
      }
      if (error.includes('Officer Last Name')) {
        errorFields.officerLastName = true;
        if (!firstErrorField) firstErrorField = 'officerLastName';
      }
      if (error.includes('Badge/ID')) {
        errorFields.badgeOrId = true;
        if (!firstErrorField) firstErrorField = 'badgeOrId';
      }
      if (error.includes('Officer Title')) {
        errorFields.officerTitle = true;
        if (!firstErrorField) firstErrorField = 'officerTitle';
      }
      if (error.includes('Officer Phone')) {
        errorFields.officerPhone = true;
        if (!firstErrorField) firstErrorField = 'officerPhone';
      }
      if (error.includes('Officer Email')) {
        errorFields.officerEmail = true;
        if (!firstErrorField) firstErrorField = 'officerEmail';
      }
      if (error.includes('Contact County')) {
        errorFields.contactCounty = true;
        if (!firstErrorField) firstErrorField = 'contactCounty';
      }
      if (error.includes('Contact Street Address')) {
        errorFields.contactStreetAddress = true;
        if (!firstErrorField) firstErrorField = 'contactStreetAddress';
      }
      if (error.includes('Contact City')) {
        errorFields.contactCity = true;
        if (!firstErrorField) firstErrorField = 'contactCity';
      }
      if (error.includes('Current State')) {
        errorFields.currentState = true;
        if (!firstErrorField) firstErrorField = 'currentState';
      }
      if (error.includes('Current Zip Code')) {
        errorFields.currentZipCode = true;
        if (!firstErrorField) firstErrorField = 'currentZipCode';
      }
      if (error.includes('Personal Phone')) {
        errorFields.phoneNumber = true;
        if (!firstErrorField) firstErrorField = 'phoneNumber';
      }
      if (error.includes('Employer Phone')) {
        errorFields.employerPhoneNumber = true;
        if (!firstErrorField) firstErrorField = 'employerPhoneNumber';
      }
    });

    // Find the earliest field in form order that has an error
    const errorFieldNames = Object.keys(errorFields);
    const sortedErrorFields = fieldOrder.filter(field => errorFieldNames.includes(field));
    const actualFirstField = sortedErrorFields[0] || firstErrorField;

    setFieldErrors(errorFields);

    // Scroll to the first invalid field (in form order)
    if (actualFirstField) {
      setTimeout(() => {
        const fieldElement = document.querySelector(`[name="${actualFirstField}"]`);
        if (fieldElement) {
          // Scroll to the field with some offset for better visibility
          fieldElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest'
          });

          // Focus the field to draw attention
          fieldElement.focus();

          // Add a temporary red glow effect
          fieldElement.style.boxShadow = '0 0 10px 2px rgba(220, 53, 69, 0.6)';
          fieldElement.style.transition = 'box-shadow 0.3s ease';

          // Remove the glow effect after 3 seconds
          setTimeout(() => {
            fieldElement.style.boxShadow = '';
          }, 3000);
        }
      }, 100); // Small delay to ensure DOM is updated
    }

    // Clear field errors after 15 seconds (increased from 10 to give users more time)
    setTimeout(() => {
      setFieldErrors({});
    }, 15000);
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked, files } = e.target;

    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({
        ...prev,
        [name]: false
      }));
    }

    // Handle file input for photo
    if (type === 'file' && name === 'photoFile') {
      const file = files[0];
      if (file) {
        // Store the file temporarily and update the form data
        setFormData(prev => ({
          ...prev,
          photoFile: file,
          photoUrl: '' // Clear any existing URL
        }));
      }
      return;
    }

    // Debug logging for dual citizenship fields
    if (name === 'hasDualCitizenship' || name === 'dualCitizenshipCountry') {
      // Dual citizenship field changed
    }

    setFormData(prev => {
      let updatedData = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      };

      // Debug DIO field
      if (name === 'is_submitter_dio') {
        console.log('Dashboard - DIO field changed:', { name, value, type });
        // Auto-populate TFM fields from existing form data when DIO is checked
        if (value === true) {
          if (prev.agencyName && !prev.tfmLawEnforcementAgency) {
            updatedData.tfmLawEnforcementAgency = prev.agencyName;
          }
          if (prev.officerPhone && !prev.tfmCellPhone) {
            updatedData.tfmCellPhone = prev.officerPhone;
          }
          if (prev.officerFirstName && prev.officerLastName && !prev.tfmTaskForceOfficerName) {
            updatedData.tfmTaskForceOfficerName = `${prev.officerFirstName} ${prev.officerLastName}`.trim();
          }
          if (prev.badgeOrId && !prev.tfmBadgeNumber) {
            updatedData.tfmBadgeNumber = prev.badgeOrId;
          }
          if (prev.contactDate && !prev.tfmArrestDate) {
            updatedData.tfmArrestDate = prev.contactDate;
          }
          // Auto-populate alien information from main form (exclude middle name to avoid "Na")
          if (prev.firstName && prev.lastName && !prev.tfmAlienName) {
            updatedData.tfmAlienName = `${prev.firstName} ${prev.lastName}`.trim();
          }
          if (prev.dateOfBirth && !prev.tfmAlienDob) {
            updatedData.tfmAlienDob = prev.dateOfBirth;
          }
          if (prev.alienRegistrationNumber && !prev.tfmAlienANumber) {
            updatedData.tfmAlienANumber = prev.alienRegistrationNumber;
          }
          if (prev.sex && !prev.tfmAlienSex) {
            updatedData.tfmAlienSex = prev.sex;
          }
          // Auto-populate nationality from Country of Citizenship
          if (prev.countryOfCitizenship && !prev.tfmAlienNationality) {
            updatedData.tfmAlienNationality = prev.countryOfCitizenship;
          }
          // Auto-populate arrest location from contact address/city (sections 3-5)
          if (!prev.tfmArrestLocation) {
            const addressParts = [];
            if (prev.contactStreetAddress) addressParts.push(prev.contactStreetAddress);
            if (prev.contactCity) addressParts.push(prev.contactCity);
            if (addressParts.length > 0) {
              updatedData.tfmArrestLocation = addressParts.join(', ');
            }
          }
        }
      }

      // Auto-populate TFM fields when related fields change (if DIO is checked)
      if (prev.is_submitter_dio === true) {
        if (name === 'officerPhone' && !prev.tfmCellPhone) {
          updatedData.tfmCellPhone = value;
        }
        if (name === 'officerFirstName' || name === 'officerLastName') {
          const firstName = name === 'officerFirstName' ? value : prev.officerFirstName;
          const lastName = name === 'officerLastName' ? value : prev.officerLastName;
          if (!prev.tfmTaskForceOfficerName) {
            updatedData.tfmTaskForceOfficerName = `${firstName} ${lastName}`.trim();
          }
        }
        if (name === 'badgeOrId' && !prev.tfmBadgeNumber) {
          updatedData.tfmBadgeNumber = value;
        }
        if (name === 'contactDate' && !prev.tfmArrestDate) {
          updatedData.tfmArrestDate = value;
        }
        // Auto-populate nationality when Country of Citizenship changes
        if (name === 'countryOfCitizenship' && !prev.tfmAlienNationality) {
          updatedData.tfmAlienNationality = value;
        }
        // Auto-populate arrest location when contact address/city changes
        if ((name === 'contactStreetAddress' || name === 'contactCity') && !prev.tfmArrestLocation) {
          const addressParts = [];
          const street = name === 'contactStreetAddress' ? value : prev.contactStreetAddress;
          const city = name === 'contactCity' ? value : prev.contactCity;
          if (street) addressParts.push(street);
          if (city) addressParts.push(city);
          if (addressParts.length > 0) {
            updatedData.tfmArrestLocation = addressParts.join(', ');
          }
        }
      }

      // Show/hide conditional fields - clear dependent fields when parent checkbox is unchecked
      if (name === 'hasDualCitizenship' && !checked) {
        // Clearing dual citizenship country field
        updatedData.dualCitizenshipCountry = '';
      }
      if (name === 'hasGangAffiliations' && !checked) {
        updatedData.gangName = '';
      }
      if (name === 'wasArrested') {
        if (value === 'Not Arrested') {
          // If not arrested, clear arrest charges and set 287(g) to "Alien was not arrested"
          updatedData.arrestCharges = '';
          updatedData.used287gAuthority = 'Alien was not arrested'; // Auto-set to new option
        } else if (value === 'Yes - Local/State Charges' || value === 'Yes - Federal Immigration Charges') {
          // If arrested, reset 287(g) to undefined to force selection
          updatedData.used287gAuthority = undefined;
        }
      }
      if (name === 'iceContacted' && (value === 'No' || value === '')) {
        updatedData.iceResponded = '';
        updatedData.detainedByIce = false;
        updatedData.notDetainedReason = '';
        updatedData.iceNoResponseReason = '';
      }

      // COMMENTED OUT: Don't auto-clear ICE fields when arrested - ICE is contacted for arrests too
      // if (name === 'wasArrested' && checked) {
      //   updatedData.iceContacted = false;
      //   updatedData.iceResponded = false;
      //   updatedData.detainedByIce = false;
      //   updatedData.notDetainedReason = '';
      //   updatedData.iceNoResponseReason = '';
      // }
      if (name === 'iceResponded' && (value === 'No' || value === 'ICE was not contacted' || value === '')) {
        updatedData.detainedByIce = false;
        updatedData.notDetainedReason = '';
        updatedData.iceNoResponseReason = '';
      }

      // Debug logging for dual citizenship fields
      if (name === 'hasDualCitizenship' || name === 'dualCitizenshipCountry') {
        // Form data updated
      }

      return updatedData;
    });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);
    setError(null);

    try {
      // ===== COMPREHENSIVE FDLE FIELD VALIDATION =====
      const validationErrors = [];

      // 1. FDLE Required Fields - Officer Contact Information
      if (!formData.ori || formData.ori.trim() === '') {
        validationErrors.push('ORI (Originating Agency Identifier) is required for FDLE submission');
      }

      if (!formData.agencyName || formData.agencyName.trim() === '') {
        validationErrors.push('Agency Name is required for FDLE submission');
      }

      if (!formData.officerFirstName || formData.officerFirstName.trim() === '') {
        validationErrors.push('Officer First Name is required for FDLE submission');
      }

      if (!formData.officerLastName || formData.officerLastName.trim() === '') {
        validationErrors.push('Officer Last Name is required for FDLE submission');
      }

      if (!formData.badgeOrId || formData.badgeOrId.trim() === '') {
        validationErrors.push('Badge/ID Number is required for FDLE submission');
      }

      if (!formData.officerTitle || formData.officerTitle.trim() === '') {
        validationErrors.push('Officer Title/Rank is required for FDLE submission');
      }

      if (!formData.officerPhone || formData.officerPhone.trim() === '') {
        validationErrors.push('Officer Phone Number is required for FDLE submission');
      }

      if (!formData.officerEmail || formData.officerEmail.trim() === '') {
        validationErrors.push('Officer Email Address is required for FDLE submission');
      }

      // 2. Contact Location Information (Required for FDLE)
      if (!formData.contactCounty || formData.contactCounty.trim() === '') {
        validationErrors.push('Contact County is required for FDLE submission');
      }

      if (!formData.contactStreetAddress || formData.contactStreetAddress.trim() === '') {
        validationErrors.push('Contact Street Address is required for FDLE submission');
      }

      if (!formData.contactCity || formData.contactCity.trim() === '') {
        validationErrors.push('Contact City is required for FDLE submission');
      }

      // 3. Validate phone numbers using centralized validation
      const phoneError = validatePhoneForSubmission(formData.phoneNumber, 'Personal Phone Number');
      if (phoneError) {
        validationErrors.push(phoneError);
      }

      const employerPhoneError = validatePhoneForSubmission(formData.employerPhoneNumber, 'Employer Phone Number');
      if (employerPhoneError) {
        validationErrors.push(employerPhoneError);
      }

      const officerPhoneError = validatePhoneForSubmission(formData.officerPhone, 'Officer Phone Number');
      if (officerPhoneError) {
        validationErrors.push(officerPhoneError);
      }

      // 4. Email validation for officer email
      const emailError = validateEmailForSubmission(formData.officerEmail, 'Officer Email Address');
      if (emailError) {
        validationErrors.push(emailError);
      }

      // 5. ORI validation (should be numeric)
      if (formData.ori && formData.ori.trim() !== '') {
        const oriRegex = /^\d+$/;
        if (!oriRegex.test(formData.ori.trim())) {
          validationErrors.push('ORI must contain only numbers');
        }
      }

      // 6. TFM Fields validation (DISABLED - fields are optional for now)
      // if (formData.is_submitter_dio === true) {
      //   // Law Enforcement Agency
      //   if (!formData.tfmLawEnforcementAgency || formData.tfmLawEnforcementAgency.trim() === '') {
      //     validationErrors.push('TFM: Law Enforcement Agency is required when DIO is checked');
      //   }
      //   // Alien Information
      //   if (!formData.tfmAlienName || formData.tfmAlienName.trim() === '') {
      //     validationErrors.push('TFM: Alien Name is required when DIO is checked');
      //   }
      //   if (!formData.tfmAlienDob || formData.tfmAlienDob.trim() === '') {
      //     validationErrors.push('TFM: Alien DOB is required when DIO is checked');
      //   }
      //   if (!formData.tfmAlienANumber || formData.tfmAlienANumber.trim() === '') {
      //     validationErrors.push('TFM: Alien A Number is required when DIO is checked');
      //   }
      //   if (!formData.tfmAlienSex || formData.tfmAlienSex.trim() === '') {
      //     validationErrors.push('TFM: Alien Sex is required when DIO is checked');
      //   }
      //   if (!formData.tfmAlienNationality || formData.tfmAlienNationality.trim() === '') {
      //     validationErrors.push('TFM: Alien Nationality is required when DIO is checked');
      //   }
      //   if (!formData.tfmAlienFbiNumber || formData.tfmAlienFbiNumber.trim() === '') {
      //     validationErrors.push('TFM: Alien FBI Number is required when DIO is checked');
      //   }
      //   // Task Force Officer Information
      //   if (!formData.tfmTaskForceOfficerName || formData.tfmTaskForceOfficerName.trim() === '') {
      //     validationErrors.push('TFM: Task Force Officer Name is required when DIO is checked');
      //   }
      //   if (!formData.tfmBadgeNumber || formData.tfmBadgeNumber.trim() === '') {
      //     validationErrors.push('TFM: Badge Number is required when DIO is checked');
      //   }
      //   if (!formData.tfmIceCredentialNumber || formData.tfmIceCredentialNumber.trim() === '') {
      //     validationErrors.push('TFM: ICE Credential Number is required when DIO is checked');
      //   }
      //   if (!formData.tfmCellPhone || formData.tfmCellPhone.trim() === '') {
      //     validationErrors.push('TFM: Cell Phone Number is required when DIO is checked');
      //   }
      //   if (!formData.tfmArrestDate || formData.tfmArrestDate.trim() === '') {
      //     validationErrors.push('TFM: Date of Arrest is required when DIO is checked');
      //   }
      //   if (!formData.tfmArrestTime || formData.tfmArrestTime.trim() === '') {
      //     validationErrors.push('TFM: Arrest Time is required when DIO is checked');
      //   }
      //   if (!formData.tfmEventNumber || formData.tfmEventNumber.trim() === '') {
      //     validationErrors.push('TFM: Event Number is required when DIO is checked');
      //   }
      //   if (!formData.tfmIceApprovalOfficerName || formData.tfmIceApprovalOfficerName.trim() === '') {
      //     validationErrors.push('TFM: ICE Approval Officer Name is required when DIO is checked');
      //   }
      //   if (!formData.tfmIceApprovalDatetime || formData.tfmIceApprovalDatetime.trim() === '') {
      //     validationErrors.push('TFM: Date/Time of ICE Approval is required when DIO is checked');
      //   }
      //   if (!formData.tfmArrestLocation || formData.tfmArrestLocation.trim() === '') {
      //     validationErrors.push('TFM: Arrest Location is required when DIO is checked');
      //   }
      //   if (!formData.tfmConsentGrantedBy || formData.tfmConsentGrantedBy.trim() === '') {
      //     validationErrors.push('TFM: Consent Granted By is required when DIO is checked');
      //   }
      //   if (!formData.tfmConsentTime || formData.tfmConsentTime.trim() === '') {
      //     validationErrors.push('TFM: Time Consent Was Granted is required when DIO is checked');
      //   }
      //   if (!formData.tfmSearchCompletedTime || formData.tfmSearchCompletedTime.trim() === '') {
      //     validationErrors.push('TFM: Time Search Completed is required when DIO is checked');
      //   }
      //   if (!formData.tfmInvestigationHours || formData.tfmInvestigationHours === '') {
      //     validationErrors.push('TFM: Hours Spent on Investigation is required when DIO is checked');
      //   }
      //   if (!formData.tfmNotes || formData.tfmNotes.trim() === '') {
      //     validationErrors.push('TFM: Notes is required when DIO is checked');
      //   }
      // }

      // If there are validation errors, show them and stop submission
      if (validationErrors.length > 0) {
        const errorMessage = `Please fix the following issues before submitting:\n\n• ${validationErrors.join('\n• ')}\n\nYour form data has been preserved.`;
        setError(errorMessage);
        showError(errorMessage);

        // Highlight invalid fields
        highlightInvalidFields(validationErrors);

        // Form data is preserved - don't clear anything
        return;
      }

      // Authentication configured

      // Use the form data directly - it already matches the API format
      const apiData = { ...formData };

      // Clean phone numbers for database storage (digits only)
      if (apiData.officerPhone) {
        apiData.officerPhone = cleanPhoneForDatabase(apiData.officerPhone);
      }
      if (apiData.phoneNumber) {
        apiData.phoneNumber = cleanPhoneForDatabase(apiData.phoneNumber);
      }
      if (apiData.employerPhoneNumber) {
        apiData.employerPhoneNumber = cleanPhoneForDatabase(apiData.employerPhoneNumber);
      }

      // Handle photo upload if a file was selected
      if (formData.photoFile) {
        // Uploading photo file
        const uploadedImagePath = await handleFileUpload(formData.photoFile);
        if (uploadedImagePath) {
          apiData.photoUrl = uploadedImagePath;
        } else {
          // If image upload failed, don't save the record
          const imageError = 'Image upload failed. Record not saved.\n\nYour form data has been preserved. Please try uploading the image again.';
          setError(imageError);
          showError(imageError);
          return; // Exit early, don't save the record - form data is preserved
        }
        // Remove the file object from the data being sent to the API
        delete apiData.photoFile;
      }

      // Debug: Log dual citizenship fields being sent
      // Dual citizenship data prepared
      // API data prepared for submission

      // Send data to API
      console.log('Dashboard CREATE - sending is_submitter_dio:', apiData.is_submitter_dio, 'is_non_criminal_arrest:', apiData.is_non_criminal_arrest);
      const createUrl = process.env.NODE_ENV === 'production' ? '/api/immigrants' : 'http://localhost:8080/api/immigrants';

      const response = await axios.post(createUrl, apiData, authHeader);



      // Only proceed if we get a successful response
      if (!response || response.status < 200 || response.status >= 300) {
        throw new Error(`Server returned status ${response.status}: ${response.statusText}`);
      }

      if (!response.data) {
        throw new Error('Server returned empty response');
      }

      // Check if the response indicates success
      if (response.data.error) {
        throw new Error(`Server error: ${response.data.error}`);
      }

      // Check if we got a record ID back (indicating successful creation)
      if (!response.data.id && !response.data.recordId && !response.data.insertId) {
        console.warn('POST response missing record ID:', response.data);
      }

      // Clear form after successful submission
      const resetFormData = async () => {
        setFormData({
          // 1. Date of Contact
          contactDate: '',

          // 2. Contacting Agency - No auto-population, user must enter manually
          ori: '',
          agencyName: '',
          is_submitter_dio: false, // Reset DIO status
          is_non_criminal_arrest: false, // Reset non-criminal arrest status
          officerFirstName: '',
          officerLastName: '',
          badgeOrId: '',
          officerTitle: '',
          officerPhone: '',
          officerEmail: getUserEmailFromToken(), // Auto-populate with logged-in user's email

        // 3-5. Location of Contact
        contactCounty: '',
        contactStreetAddress: '',
        contactApartmentUnit: '',
        contactCity: '',

        // 6. Illegal Alien's Personal Information
        firstName: '',
        middleName: '',
        lastName: '',
        countryOfCitizenship: '',
        hasDualCitizenship: false,
        dualCitizenshipCountry: '',
        alienRegistrationNumber: '',
        dateOfBirth: '',
        sex: '',

        // 7. Illegal Alien's Current Address
        currentStreetAddress: '',
        currentCity: '',
        currentState: '',
        currentZipCode: '',
        phoneNumber: '',

        // 8. Illegal Alien's Current Employer
        employerName: '',
        employerStreetAddress: '',
        employerCity: '',
        employerState: '',
        employerZipCode: '',
        employerPhoneNumber: '',

        // 9. Gang Affiliations
        hasGangAffiliations: undefined, // Force user to select
        gangName: '',

        // 9.5. Contact Description
        contactDescription: '',

        // 9.6. Criminal History (required)
        hasCriminalHistory: undefined, // Force user to select

        // 9.7. Arrest Information (required)
        wasArrested: undefined, // Force user to select

        // 9.8. 287(g) Authority Usage (conditional - only if arrested)
        used287gAuthority: undefined, // Force user to select if arrested

        // 10. Vehicle Information
        vehicleYear: '',
        vehicleMake: '',
        vehicleModel: '',
        vehicleTag: '',
        vehicleTagState: '',

        // 12. Citizenship Status Verification
        citizenshipVerifiedWithIce: false,
        iceCallCenterContactName: '',
        iceCallCenterContactId: '',

        // 13. Arrest Information
        arrestCharges: '',
        iceContacted: false,
        iceResponded: false,
        detainedByIce: false,
        notDetainedReason: '',
        iceNoResponseReason: '',

          // 14. Photo
          photoUrl: '',
          photoFile: null
        });
      };

      // Call the reset function
      resetFormData();

      // Clear the file input element
      if (photoFileInputRef.current) {
        photoFileInputRef.current.value = '';
      }

      // Refresh the records by triggering a re-fetch
      // Refreshing records list
      setCurrentPage(1);
      setRefreshTrigger(prev => prev + 1);

      // Add a small delay to ensure the refresh completes
      setTimeout(() => {
        showSuccess('Record added successfully!');
      }, 500);
    } catch (err) {
      console.error('Error adding record:', err);

      // Extract detailed error message
      let errorMessage = 'Failed to add record. Please try again.';
      if (err.response?.data?.error) {
        errorMessage = `Error adding record: ${err.response.data.error}`;
      } else if (err.response?.data?.message) {
        errorMessage = `Error adding record: ${err.response.data.message}`;
      } else if (err.message) {
        errorMessage = `Error adding record: ${err.message}`;
      }

      setError(errorMessage);
      showError(errorMessage + '\n\nYour form data has been preserved. Please fix the issues and try again.');

      // Do NOT clear form data or show any success messages when there's an error
      // Form data is preserved so user doesn't lose their work
      return;
    } finally {
      setLoading(false);
    }
  };

  // Handle search with debounce
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page on new search
  };

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Handle view record button click
  const handleViewRecord = async (record, context = null) => {
    // View button clicked for record

    setSelectedRecord(null);
    setModalVisible(true);
    setViewContext(context); // Set the context for the modal

    try {
      // Fetch the full record details from the API
      const viewUrl = process.env.NODE_ENV === 'production' ? `/api/immigrants/${record.id}` : `http://localhost:8080/api/immigrants/${record.id}`;
      const response = await axios.get(viewUrl, authHeader);
      // Record data retrieved successfully

      // Set the selected record from the API response
      setSelectedRecord(response.data);
    } catch (err) {
      console.error('Error fetching record details:', err);
      // Fall back to using the record from the table if API call fails
      setSelectedRecord(record);
    }
  };

  // Handle add detention record button click
  const handleAddDetentionRecord = async (record) => {
    // Add Detention Record button clicked

    try {
      // Fetch the full record details from the API
      const viewUrl = process.env.NODE_ENV === 'production' ? `/api/immigrants/${record.id}` : `http://localhost:8080/api/immigrants/${record.id}`;
      const response = await axios.get(viewUrl, authHeader);
      // Detention record data retrieved successfully

      // Set the selected immigrant and show detention modal
      setSelectedImmigrantForDetention(response.data);
      setDetentionModalVisible(true);
    } catch (err) {
      console.error('Error fetching record details for detention:', err);
      // Fall back to using the record from the table if API call fails
      setSelectedImmigrantForDetention(record);
      setDetentionModalVisible(true);
    }
  };

  // Handle detention record creation
  const handleCreateDetentionRecord = async (detentionData) => {
    try {
      const createUrl = process.env.NODE_ENV === 'production' ? '/api/detention' : 'http://localhost:8080/api/detention';
      const response = await axios.post(createUrl, detentionData, authHeader);

      // Detention record created successfully

      // Close the modal
      setDetentionModalVisible(false);

      setSelectedImmigrantForDetention(null);

      // Show success message
      showSuccess('Detention record created successfully!');

      // Refresh detention records if we're on that tab
      if (activeTab === 'view-detention-records') {
        fetchDetentionRecords(detentionCurrentPage);
      }

      // Refresh detention list records if we're on the add detention record tab
      if (activeTab === 'add-detention-record') {
        // Reset to first page and trigger refresh
        setDetentionListCurrentPage(1);
        // Force refresh by incrementing the trigger
        setDetentionListRefreshTrigger(prev => prev + 1);
      }

    } catch (err) {
      console.error('Error creating detention record:', err);
      const errorMessage = err.response?.data?.error || 'Failed to create detention record';
      showError(`Error: ${errorMessage}`);
    }
  };

  // Fetch detention records
  const fetchDetentionRecords = useCallback(async (page = 1) => {
    setDetentionLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '10'
      });

      if (detentionFilters.month) params.append('month', detentionFilters.month);
      if (detentionFilters.facilityType) params.append('facility_type', detentionFilters.facilityType);
      if (detentionFilters.releasedMonth) params.append('released_month', detentionFilters.releasedMonth);
      if (detentionFilters.arrestingAgency) params.append('arresting_agency', detentionFilters.arrestingAgency);
      if (detentionFilters.iceCustodyPending) params.append('ice_custody_pending', 'true');
      if (detentionFilters.releasedHousingCostsUpdates) params.append('released_housing_costs_updates', 'true');
      if (detentionFilters.housingCostReimbursementReceived) params.append('housing_cost_reimbursement_received', 'true');
      if (detentionSearchTerm && detentionSearchTerm.trim()) params.append('search', detentionSearchTerm.trim());



      const apiUrl = process.env.NODE_ENV === 'production'
        ? `/api/detention?${params}`
        : `http://localhost:8080/api/detention?${params}`;

      const response = await axios.get(apiUrl, authHeader);

      setDetentionRecords(response.data.records);
      setDetentionCurrentPage(response.data.pagination.currentPage);
      setDetentionTotalPages(response.data.pagination.totalPages);
      setDetentionTotalCount(response.data.pagination.totalCount);

      // Use available agencies from the response
      if (response.data.availableAgencies) {
        setAvailableArrestingAgencies(response.data.availableAgencies);
      }
    } catch (error) {
      console.error('Error fetching detention records:', error);
      showError('Failed to fetch detention records');
    } finally {
      setDetentionLoading(false);
    }
  }, [authHeader, detentionFilters.month, detentionFilters.facilityType, detentionFilters.releasedMonth, detentionFilters.arrestingAgency, detentionFilters.iceCustodyPending, detentionFilters.releasedHousingCostsUpdates, detentionFilters.housingCostReimbursementReceived, detentionSearchTerm]);

  // Fetch detention records when tab changes or pagination changes
  useEffect(() => {
    if (activeTab === 'view-detention-records' && token && hasDetentionAccess) {
      fetchDetentionRecords(detentionCurrentPage);
    }
  }, [activeTab, detentionCurrentPage, token, hasDetentionAccess, fetchDetentionRecords]);

  // Auto-apply checkbox filters when they change
  useEffect(() => {
    if (activeTab === 'view-detention-records' && token && hasDetentionAccess) {
      fetchDetentionRecords(1);
    }
  }, [
    detentionFilters.iceCustodyPending,
    detentionFilters.releasedHousingCostsUpdates,
    detentionFilters.housingCostReimbursementReceived,
    activeTab,
    token,
    hasDetentionAccess,
    fetchDetentionRecords
  ]);

  // Handle view detention record button click
  const handleViewDetentionRecord = async (record) => {
    // View button clicked for detention record

    setSelectedDetentionRecord(null);
    setDetentionRecordModalVisible(true);

    try {
      const viewUrl = process.env.NODE_ENV === 'production'
        ? `/api/detention/${record.id}`
        : `http://localhost:8080/api/detention/${record.id}`;
      const response = await axios.get(viewUrl, authHeader);
      // Detention record retrieved successfully

      setSelectedDetentionRecord(response.data);
    } catch (err) {
      console.error('Error fetching detention record details:', err);
      showError('Failed to fetch record details');
      setSelectedDetentionRecord(record);
    }
  };

  // Handle edit detention record button click
  const handleEditDetentionRecord = async (record) => {
    // Edit button clicked for detention record

    // Reset state first
    setSelectedDetentionRecordForEdit(null);
    setEditDetentionModalVisible(false);

    try {
      // Fetch fresh data from the API
      const viewUrl = process.env.NODE_ENV === 'production'
        ? `/api/detention/${record.id}`
        : `http://localhost:8080/api/detention/${record.id}`;
      const response = await axios.get(viewUrl, authHeader);
      // Detention record data loaded successfully

      // Set the record data first, then open the modal
      setSelectedDetentionRecordForEdit(response.data);

      // Small delay to ensure state is updated before opening modal
      setTimeout(() => {
        setEditDetentionModalVisible(true);
      }, 100);
    } catch (err) {
      console.error('Error fetching detention record details for edit:', err);
      showError('Failed to fetch record details');
      // Fallback to using the passed record if API fails
      setSelectedDetentionRecordForEdit(record);
      setEditDetentionModalVisible(true);
    }
  };

  // Handle delete detention record - Admin and Security Admin only
  const handleDeleteDetentionRecord = async (record) => {
    const confirmed = await showConfirm(
      `Are you sure you want to permanently delete the detention record for "${record.facility_name}"? This action cannot be undone.`,
      'Confirm Detention Record Deletion',
      'Delete',
      'Cancel'
    );

    if (!confirmed) return;

    try {
      const apiUrl = process.env.NODE_ENV === 'production'
        ? `/api/detention/${record.id}`
        : `http://localhost:8080/api/detention/${record.id}`;

      await axios.delete(apiUrl, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      showSuccess('Detention record deleted successfully');

      // Update local state immediately
      setDetentionRecords(prevRecords => prevRecords.filter(r => r.id !== record.id));

    } catch (error) {
      console.error('Error deleting detention record:', error);
      if (error.response?.status === 403) {
        showError('You do not have permission to delete detention records.');
      } else if (error.response?.status === 404) {
        showError('Detention record not found.');
      } else {
        showError('Failed to delete detention record. Please try again.');
      }
    }
  };

  // Handle update detention record
  const handleUpdateDetentionRecord = async (updatedData) => {
    try {
      // Updating detention record
      // Selected record for edit prepared
      // Detention record update data prepared

      if (!selectedDetentionRecordForEdit?.id) {
        throw new Error('No detention record selected for editing');
      }

      const updateUrl = process.env.NODE_ENV === 'production'
        ? `/api/detention/${selectedDetentionRecordForEdit.id}`
        : `http://localhost:8080/api/detention/${selectedDetentionRecordForEdit.id}`;

      const response = await axios.put(updateUrl, updatedData, authHeader);
      // Detention record updated successfully

      // Update the detention records list
      setDetentionRecords(prevRecords => {
        const updatedRecords = prevRecords.map(record =>
          record.id === selectedDetentionRecordForEdit.id
            ? { ...record, ...response.data }
            : record
        );
        // Updated detention records list
        return updatedRecords;
      });

      // Close the edit modal
      setEditDetentionModalVisible(false);
      setSelectedDetentionRecordForEdit(null);

      showSuccess('Detention record updated successfully');

      // Refresh the detention records to ensure we have the latest data
      fetchDetentionRecords(detentionCurrentPage);

      return response.data;
    } catch (err) {
      console.error('Error updating detention record:', err);
      showError('Failed to update detention record: ' + (err.response?.data?.error || err.message));
      throw err;
    }
  };

  // Handle detention filter changes
  const handleDetentionFilterChange = (e) => {
    const { name, value, type, checked } = e.target;
    setDetentionFilters(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Auto-apply filters for checkboxes
    if (type === 'checkbox') {
      setDetentionCurrentPage(1);
    }
  };

  // Apply detention filters
  const applyDetentionFilters = () => {
    setDetentionCurrentPage(1);
    fetchDetentionRecords(1);
  };

  // Clear detention filters
  const clearDetentionFilters = async () => {
    setDetentionFilters({
      month: '',
      facilityType: '',
      releasedMonth: '',
      arrestingAgency: '',
      iceCustodyPending: false,
      releasedHousingCostsUpdates: false,
      housingCostReimbursementReceived: false
    });
    setDetentionSearchTerm('');
    setDetentionCurrentPage(1);
    fetchDetentionRecords(1);
  };

  // Handle standalone detention record creation
  const handleCreateStandaloneDetentionRecord = async (formData) => {
    try {
      // Creating standalone detention record

      const createUrl = process.env.NODE_ENV === 'production'
        ? '/api/detention'
        : 'http://localhost:8080/api/detention';

      const response = await axios.post(createUrl, formData, authHeader);
      // Standalone detention record created successfully

      showSuccess('Standalone detention record created successfully');

      // Close the modal
      setStandaloneDetentionModalVisible(false);

      // Refresh detention records if we're on that tab
      if (activeTab === 'view-detention-records') {
        fetchDetentionRecords(detentionCurrentPage);
      }

      // Refresh detention list records if we're on the add detention record tab
      if (activeTab === 'add-detention-record') {
        // Reset to first page and trigger refresh
        setDetentionListCurrentPage(1);
        // Force refresh by incrementing the trigger
        setDetentionListRefreshTrigger(prev => prev + 1);
      }

      return response.data;
    } catch (err) {
      console.error('Error creating standalone detention record:', err);
      showError('Failed to create standalone detention record: ' + (err.response?.data?.error || err.message));
      throw err;
    }
  };

  // Format date for detention records display
  const formatDetentionDate = (dateString) => {
    if (!dateString) return 'N/A';

    // Handle PostgreSQL DATE format (YYYY-MM-DD) to avoid timezone issues
    // PostgreSQL DATE fields are returned as strings like "2025-07-01"
    if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateString.split('-');
      // Create date in local timezone to avoid UTC conversion issues
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long'
      });
    }

    // Handle month-only format (YYYY-MM)
    if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}$/)) {
      const [year, month] = dateString.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1, 1);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long'
      });
    }

    // Fallback for other date formats
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long'
    });
  };



  // Handle approve record
  const handleApproveRecord = async (recordId) => {
    try {
      // Find the record in the unapproved list
      const recordToApprove = records.find(record => record.id === recordId);

      if (recordToApprove) {
        // First mark the record as being approved to trigger animation
        setRecords(prevRecords =>
          prevRecords.map(record =>
            record.id === recordId
              ? { ...record, isBeingApproved: true }
              : record
          )
        );

        // Wait a short time for the animation to play
        await new Promise(resolve => setTimeout(resolve, 500));

        // Then remove it from the unapproved list
        setRecords(prevRecords => prevRecords.filter(record => record.id !== recordId));

        // Add the record to the approved list with is_approved set to true and mark as newly approved
        const approvedRecord = { ...recordToApprove, is_approved: true, newlyApproved: true };
        setApprovedRecords(prevRecords => {
          // Insert the record in the correct position to maintain ID-based sorting (lowest first)
          const newRecords = [...prevRecords, approvedRecord];
          return newRecords.sort((a, b) => a.id - b.id);
        });

        // Clear the newlyApproved flag after animation completes
        setTimeout(() => {
          setApprovedRecords(prevRecords =>
            prevRecords.map(record =>
              record.id === recordId ? { ...record, newlyApproved: false } : record
            )
          );
        }, 1500);
      }

      // Call the API to approve the record
      const approveUrl = process.env.NODE_ENV === 'production' ? `/api/immigrants/${recordId}/approve` : `http://localhost:8080/api/immigrants/${recordId}/approve`;
      const response = await axios.patch(approveUrl, {}, authHeader);

      // Record approved successfully

      // Refresh both record lists to ensure data consistency
      setCurrentPage(1);
      setApprovedCurrentPage(1);
      setRefreshTrigger(prev => prev + 1);

      return response.data;
    } catch (err) {
      console.error('Error approving record:', err);
      // If there was an error, refresh both lists to restore the correct state
      setCurrentPage(1);
      setApprovedCurrentPage(1);
      setRefreshTrigger(prev => prev + 1);
      throw err;
    }
  };

  // Handle update record
  const handleUpdateRecord = async (updatedRecord) => {
    try {
      // Updating record

      // Ensure all required fields are present
      const requiredFields = [
        'contactDate', 'ori', 'agencyName', 'officerFirstName', 'officerLastName', 'badgeOrId',
        'contactCounty', 'contactStreetAddress', 'contactCity',
        'firstName', 'lastName', 'countryOfCitizenship', 'dateOfBirth', 'sex'
      ];

      // Check for missing fields
      const missingFields = requiredFields.filter(field =>
        !updatedRecord[field] && updatedRecord[field] !== 0
      );

      if (missingFields.length > 0) {
        const errorMsg = `Missing required fields: ${missingFields.join(', ')}`;
        console.error(errorMsg);
        showError(errorMsg);
        throw new Error(errorMsg);
      }

      // Clean phone numbers for database storage (digits only)
      const cleanedRecord = { ...updatedRecord };
      if (cleanedRecord.officerPhone) {
        cleanedRecord.officerPhone = cleanPhoneForDatabase(cleanedRecord.officerPhone);
      }
      if (cleanedRecord.phoneNumber || cleanedRecord.phone_number) {
        const phoneField = cleanedRecord.phoneNumber || cleanedRecord.phone_number;
        cleanedRecord.phoneNumber = cleanPhoneForDatabase(phoneField);
        cleanedRecord.phone_number = cleanPhoneForDatabase(phoneField);
      }
      if (cleanedRecord.employerPhoneNumber || cleanedRecord.employer_phone_number) {
        const empPhoneField = cleanedRecord.employerPhoneNumber || cleanedRecord.employer_phone_number;
        cleanedRecord.employerPhoneNumber = cleanPhoneForDatabase(empPhoneField);
        cleanedRecord.employer_phone_number = cleanPhoneForDatabase(empPhoneField);
      }

      // Call the API to update the record
      console.log('Dashboard UPDATE - sending is_submitter_dio:', cleanedRecord.is_submitter_dio);
      const updateUrl = process.env.NODE_ENV === 'production' ? `/api/immigrants/${updatedRecord.id}` : `http://localhost:8080/api/immigrants/${updatedRecord.id}`;
      const response = await axios.put(updateUrl, cleanedRecord, authHeader);

      // Record updated successfully

      // Update the selected record with the response data
      setSelectedRecord(response.data);

      // If the record is in the unapproved list, update it there
      if (!updatedRecord.is_approved) {
        setRecords(prevRecords => {
          const updatedRecords = prevRecords.map(record =>
            record.id === updatedRecord.id
              ? { ...record,
                  firstName: response.data.first_name,
                  lastName: response.data.last_name,
                  countryOfCitizenship: response.data.country_of_citizenship,
                  contactDate: response.data.contact_date,
                  agencyName: response.data.agency_name,
                  photo_url: response.data.photo_url,
                  photoUrl: response.data.photo_url
                }
              : record
          );
          // Maintain ID-based sorting (lowest first)
          return updatedRecords.sort((a, b) => a.id - b.id);
        });
      }

      // If the record is in the approved list, update it there
      if (updatedRecord.is_approved) {
        setApprovedRecords(prevRecords => {
          const updatedRecords = prevRecords.map(record =>
            record.id === updatedRecord.id
              ? { ...record,
                  firstName: response.data.first_name,
                  lastName: response.data.last_name,
                  countryOfCitizenship: response.data.country_of_citizenship,
                  photo_url: response.data.photo_url,
                  photoUrl: response.data.photo_url
                }
              : record
          );
          // Maintain ID-based sorting (lowest first)
          return updatedRecords.sort((a, b) => a.id - b.id);
        });
      }

      return response.data;
    } catch (err) {
      console.error('Error updating record:', err);
      showError('Failed to update record: ' + (err.response?.data?.error || err.message));
      throw err;
    }
  };

  // Close modal
  const closeModal = () => {
    setModalVisible(false);
    setSelectedRecord(null);
    setViewContext(null);
  };









  // Delete record function
  const deleteRecord = async (recordId) => {
    const startTime = Date.now();
    // Starting delete operation

    try {
      const apiUrl = process.env.NODE_ENV === 'production'
        ? `/api/immigrants/${recordId}`
        : `http://localhost:8080/api/immigrants/${recordId}`;

      const response = await axios.delete(apiUrl, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const duration = Date.now() - startTime;
      // Record deletion successful

      // Remove the record from the local state
      setRecords(records.filter(record => record.id !== recordId));
      setApprovedRecords(approvedRecords.filter(record => record.id !== recordId));

      showSuccess('Record deleted successfully.');

      // Log successful deletion completion
      // Local state updated - record removed from UI

    } catch (err) {
      const duration = Date.now() - startTime;
      console.error(`[AUDIT] Record deletion failed - ID: ${recordId}, Duration: ${duration}ms, Error:`, err);

      if (err.response?.status === 403) {
        // Delete operation denied - insufficient permissions
        showError('You do not have permission to delete records.');
      } else if (err.response?.status === 404) {
        // Delete operation failed - record not found
        showError('Record not found. It may have already been deleted.');
      } else {
        // Delete operation failed - server error
        showError('Failed to delete record. Please try again.');
      }
    }
  };

  // Delete record with confirmation - Admin only
  const handleDeleteRecord = async (record) => {
    // Log the delete attempt initiation for audit purposes
    // Admin user initiated delete action

    const confirmed = await showConfirm(
      `Are you sure you want to permanently delete the record for "${record.firstName} ${record.lastName}"? This action cannot be undone and will completely remove the record from the system.`,
      'Confirm Record Deletion',
      'Delete',
      'Cancel'
    );

    if (!confirmed) {
      // Log cancellation for audit purposes
      // Admin user cancelled delete action
      return;
    }

    // Log confirmation for audit purposes
    // Admin user confirmed delete action

    await deleteRecord(record.id);
  };

  // Show loading indicator
  if (loading && records.length === 0) {
    return (
      <div className="dashboardPage">
        <header className="dashboardHeader">
          <img
            src="/trans_logo.png"
            alt="Logo"
            className="dashLogo"
          />
          <h1>Suspected Alien Information System</h1>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <button onClick={handleLogout} className="logoutBtn">Log Out</button>
          </div>
        </header>
        <div className="dashboardContent" style={{ justifyContent: 'center', alignItems: 'center' }}>
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <h2>Loading data...</h2>
            <p>Please wait while we fetch the records.</p>
          </div>
        </div>
      </div>
    );
  }

  if (!token) {
    return (
      <div className="loginPage">
        <img
          src="/GradyStar.png"
          alt="Logo"
          className="logo"
        />
        <div className="loginForm text-center">
          <p>Please <strong onClick={() => nav('/login')} style={{cursor:'pointer'}}>log in</strong> first.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboardPage">
      <header className="dashboardHeader">
        <img
          src="/GradyStar.png"
          alt="Logo"
          className="dashLogo"
        />
        <h1>Suspected Alien Information System</h1>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <span style={{ color: '#fff', fontSize: '14px', marginRight: '10px' }}>
            Role: <strong>{userRole || 'Loading...'}</strong>
          </span>

          <button onClick={handleLogout} className="logoutBtn">Log Out</button>
        </div>
      </header>

      {/* Tab Navigation */}
      <div className="tabNavigation">
        {/* Detention users get detention-specific tabs */}
        {isDetention ? (
          <>
            <button
              className={`tabBtn ${activeTab === 'add-detention-record' ? 'active' : ''}`}
              onClick={() => setActiveTab('add-detention-record')}
            >
              Add Detention Record
            </button>
            <button
              className={`tabBtn ${activeTab === 'view-detention-records' ? 'active' : ''}`}
              onClick={() => setActiveTab('view-detention-records')}
            >
              View Detention Records
            </button>
            <button
              className={`tabBtn ${activeTab === 'monthly-summary' ? 'active' : ''}`}
              onClick={() => setActiveTab('monthly-summary')}
            >
              Monthly Summary
            </button>

          </>
        ) : (
          <>
            {/* 1. Add New Record Tab */}
            <button
              className={`tabBtn ${activeTab === 'add-record' ? 'active' : ''}`}
              onClick={() => setActiveTab('add-record')}
            >
              Add New Record
            </button>

            {/* 2. All Records Tab - Officers and above */}
            {(userRole === 'Officer' || userRole === 'Admin' || userRole === 'Security Admin') && (
              <button
                className={`tabBtn ${activeTab === 'all-records' ? 'active' : ''}`}
                onClick={() => setActiveTab('all-records')}
              >
                All Records
              </button>
            )}

            {/* 3. Records Needing Approval Tab - Supervisors and above */}
            {(isSupervisor || isAdmin || isSecurityAdmin) && (
              <button
                className={`tabBtn ${activeTab === 'pending-approval' ? 'active' : ''}`}
                onClick={() => setActiveTab('pending-approval')}
              >
                Records Needing Approval
              </button>
            )}

            {/* 4. Approved Records Tab - Supervisors and above */}
            {(isSupervisor || isAdmin || isSecurityAdmin) && (
              <button
                className={`tabBtn ${activeTab === 'approved-records' ? 'active' : ''}`}
                onClick={() => setActiveTab('approved-records')}
              >
                Approved Records
              </button>
            )}

            {/* 5. Add Detention Record Tab - Detention, Admin, and Security Admin roles */}
            {(isDetention || isAdmin || isSecurityAdmin) && (
              <button
                className={`tabBtn ${activeTab === 'add-detention-record' ? 'active' : ''}`}
                onClick={() => setActiveTab('add-detention-record')}
              >
                Add Detention Record
              </button>
            )}

            {/* 6. View Detention Records Tab - Detention, Admin, and Security Admin roles */}
            {(isDetention || isAdmin || isSecurityAdmin) && (
              <button
                className={`tabBtn ${activeTab === 'view-detention-records' ? 'active' : ''}`}
                onClick={() => setActiveTab('view-detention-records')}
              >
                View Detention Records
              </button>
            )}

            {/* 7. Monthly Summary Tab - Detention, Admin, and Security Admin roles */}
            {(isDetention || isAdmin || isSecurityAdmin) && (
              <button
                className={`tabBtn ${activeTab === 'monthly-summary' ? 'active' : ''}`}
                onClick={() => setActiveTab('monthly-summary')}
              >
                Monthly Summary
              </button>
            )}

            {/* 8. User Management Tab - Admins and Security Admins only */}
            {(isAdmin || isSecurityAdmin) && (
              <button
                className={`tabBtn ${activeTab === 'user-management' ? 'active' : ''}`}
                onClick={() => setActiveTab('user-management')}
              >
                User Management
              </button>
            )}

            {/* 9. Audit Dashboard Tab - Security Admins only */}
            {isSecurityAdmin && (
              <button
                className={`tabBtn ${activeTab === 'audit-dashboard' ? 'active' : ''}`}
                onClick={() => setActiveTab('audit-dashboard')}
              >
                Audit Dashboard
              </button>
            )}


          </>
        )}
      </div>

      <div className="dashboardContent">

        {/* Add Detention Record Tab - Detention, Admin, and Security Admin only */}
        {activeTab === 'add-detention-record' && (isDetention || isAdmin || isSecurityAdmin) && (
          <div className="tabPanel">
            <h2>Add Detention Record</h2>

            {/* Action buttons */}
            <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'center' }}>
              <button
                className="modern-btn"
                onClick={() => setStandaloneDetentionModalVisible(true)}
                style={{ backgroundColor: '#4CAF50', color: 'white', padding: '12px 24px', fontSize: '16px' }}
              >
                + Create New Standalone Detention Record
              </button>
            </div>

            <p style={{ marginBottom: '20px', color: '#666' }}>
              Select an approved immigrant record below to create a linked detention record with pre-populated information.
            </p>

            <div className="searchBar">
              <input
                type="text"
                placeholder="Search approved records..."
                value={detentionListSearchTerm}
                onChange={(e) => {
                  setDetentionListSearchTerm(e.target.value);
                  setDetentionListCurrentPage(1);
                }}
                className="modern-input"
              />
            </div>

            <div className="recordsTable">
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Citizenship</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {detentionListRecords.length > 0 ? (
                    detentionListRecords.map((record) => (
                        <tr key={record.id} className={record.newlyApproved ? 'record-newly-approved' : ''}>
                          <td>{record.id}</td>
                          <td>{`${record.firstName} ${record.lastName}`}</td>
                          <td>{record.countryOfCitizenship}</td>
                          <td>
                            <div className="actionButtons">
                              <button
                                className="actionBtn viewBtn"
                                onClick={() => {
                                  const recordCopy = JSON.parse(JSON.stringify(record));
                                  handleViewRecord(recordCopy);
                                }}
                              >
                                View
                              </button>
                              <button
                                className="actionBtn detentionBtn"
                                onClick={() => handleAddDetentionRecord(record)}
                                style={{
                                  backgroundColor: '#ff9800',
                                  color: 'white',
                                  marginLeft: '5px'
                                }}
                                title="Add Detention Record"
                              >
                                Detention
                              </button>



                            </div>
                          </td>
                        </tr>
                      ))
                  ) : (
                    <tr>
                      <td colSpan="4" style={{textAlign: 'center'}}>
                        {loadingDetentionList ? 'Loading...' : 'No approved records'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination for detention list records */}
            <div className="pagination">
              <button
                onClick={() => setDetentionListCurrentPage(detentionListCurrentPage - 1)}
                disabled={detentionListCurrentPage === 1 || loadingDetentionList}
                className="paginationBtn"
              >
                Previous
              </button>
              <span>
                {loadingDetentionList ? 'Loading...' : `Page ${detentionListCurrentPage} of ${detentionListTotalPages || 1}`}
              </span>
              <button
                onClick={() => setDetentionListCurrentPage(detentionListCurrentPage + 1)}
                disabled={detentionListCurrentPage === detentionListTotalPages || detentionListTotalPages === 0 || loadingDetentionList}
                className="paginationBtn"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* View Detention Records Tab - Detention, Admin, and Security Admin only */}
        {activeTab === 'view-detention-records' && (isDetention || isAdmin || isSecurityAdmin) && (
          <div className="tabPanel">
            <h2>Detention Records</h2>

            {/* Search Bar */}
            <div className="searchBar">
              <input
                type="text"
                placeholder="Search by facility name, county, case number, FBI number, or name..."
                value={detentionSearchTerm}
                onChange={(e) => {
                  setDetentionSearchTerm(e.target.value);
                  setDetentionCurrentPage(1); // Reset to first page when searching
                }}
                className="modern-input"
              />
            </div>

            {/* Filters */}
            <div className="filtersSection">
              <div className="filterRow">
                <div className="filterGroup">
                  <label>Filter by Month:</label>
                  <input
                    type="month"
                    name="month"
                    value={detentionFilters.month}
                    onChange={handleDetentionFilterChange}
                    className="modern-input"
                  />
                </div>

                <div className="filterGroup">
                  <label>Filter by Facility Type:</label>
                  <select
                    name="facilityType"
                    value={detentionFilters.facilityType}
                    onChange={handleDetentionFilterChange}
                    className="modern-input"
                  >
                    <option value="">All Types</option>
                    <option value="COUNTY_JAIL">County Jail</option>
                    <option value="FDOC">FDOC</option>
                  </select>
                </div>

                <div className="filterGroup">
                  <label>Filter by Released Month:</label>
                  <input
                    type="month"
                    name="releasedMonth"
                    value={detentionFilters.releasedMonth}
                    onChange={handleDetentionFilterChange}
                    className="modern-input"
                  />
                </div>

                <div className="filterGroup">
                  <label>Filter by Arresting Agency:</label>
                  <select
                    name="arrestingAgency"
                    value={detentionFilters.arrestingAgency}
                    onChange={handleDetentionFilterChange}
                    className="modern-input"
                  >
                    <option value="">All Agencies</option>
                    {availableArrestingAgencies.map((agency) => (
                      <option key={agency} value={agency}>
                        {agency}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Checkbox Filters Row */}
              <div className="filterRow" style={{ marginTop: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div className="filterGroup" style={{ display: 'flex', flexDirection: 'column', gap: '10px', flex: '1' }}>
                  <label style={{ fontWeight: '500', marginBottom: '5px' }}>
                    Quick Filters:
                    {(detentionFilters.iceCustodyPending || detentionFilters.releasedHousingCostsUpdates || detentionFilters.housingCostReimbursementReceived) && (
                      <span style={{
                        marginLeft: '8px',
                        padding: '2px 6px',
                        background: '#4caf50',
                        color: 'white',
                        borderRadius: '10px',
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}>
                        {[detentionFilters.iceCustodyPending, detentionFilters.releasedHousingCostsUpdates, detentionFilters.housingCostReimbursementReceived].filter(Boolean).length} active
                      </span>
                    )}
                  </label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
                    <label className="checkboxLabel" style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 12px',
                      background: detentionFilters.iceCustodyPending ? '#e3f2fd' : '#f8f9fa',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      border: detentionFilters.iceCustodyPending ? '1px solid #2196f3' : '1px solid #e9ecef',
                      transition: 'all 0.2s ease',
                      fontWeight: detentionFilters.iceCustodyPending ? '500' : 'normal'
                    }}>
                      <input
                        type="checkbox"
                        name="iceCustodyPending"
                        checked={detentionFilters.iceCustodyPending}
                        onChange={handleDetentionFilterChange}
                        style={{ margin: '0', transform: 'scale(1.1)' }}
                      />
                      ICE Custody Pending
                    </label>

                    <label className="checkboxLabel" style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 12px',
                      background: detentionFilters.releasedHousingCostsUpdates ? '#fff3e0' : '#f8f9fa',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      border: detentionFilters.releasedHousingCostsUpdates ? '1px solid #ff9800' : '1px solid #e9ecef',
                      transition: 'all 0.2s ease',
                      fontWeight: detentionFilters.releasedHousingCostsUpdates ? '500' : 'normal'
                    }}>
                      <input
                        type="checkbox"
                        name="releasedHousingCostsUpdates"
                        checked={detentionFilters.releasedHousingCostsUpdates}
                        onChange={handleDetentionFilterChange}
                        style={{ margin: '0', transform: 'scale(1.1)' }}
                      />
                      Pending Housing Updates
                    </label>

                    <label className="checkboxLabel" style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 12px',
                      background: detentionFilters.housingCostReimbursementReceived ? '#f3e5f5' : '#f8f9fa',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      border: detentionFilters.housingCostReimbursementReceived ? '1px solid #9c27b0' : '1px solid #e9ecef',
                      transition: 'all 0.2s ease',
                      fontWeight: detentionFilters.housingCostReimbursementReceived ? '500' : 'normal'
                    }}>
                      <input
                        type="checkbox"
                        name="housingCostReimbursementReceived"
                        checked={detentionFilters.housingCostReimbursementReceived}
                        onChange={handleDetentionFilterChange}
                        style={{ margin: '0', transform: 'scale(1.1)' }}
                      />
                      Pending Reimbursement
                    </label>
                  </div>
                </div>

                {/* Filter Actions aligned to the right */}
                <div className="filterActions" style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', marginLeft: '20px' }}>
                  <button
                    onClick={applyDetentionFilters}
                    className="modern-btn"
                    style={{ minWidth: '120px', padding: '8px 16px' }}
                  >
                    Apply <br></br>Filters
                  </button>
                  <button
                    onClick={clearDetentionFilters}
                    className="modern-btn secondary"
                    style={{ minWidth: '120px', padding: '8px 16px' }}
                  >
                    Clear <br></br>Filters
                  </button>
                </div>
              </div>
            </div>

            {/* Records Table */}
            <div className="recordsTable">
              <table>
                <thead>
                  <tr>
                    <th>Report Month</th>
                    <th>Facility Name</th>
                    <th>Case Number</th>
                    <th>Arresting Agency</th>
                    <th>Linked Alien</th>
                    <th>ICE Detainer</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {detentionLoading ? (
                    <tr>
                      <td colSpan="7" style={{ textAlign: 'center' }}>
                        Loading records...
                      </td>
                    </tr>
                  ) : detentionRecords.length > 0 ? (
                    detentionRecords.map((record) => (
                      <tr key={record.id}>
                        <td>{formatDetentionDate(record.report_month)}</td>
                        <td title={record.facility_name}>
                          {record.facility_name && record.facility_name.length > 30
                            ? record.facility_name.substring(0, 30) + '...'
                            : record.facility_name}
                        </td>
                        <td>{record.case_number || 'N/A'}</td>
                        <td>{record.arresting_agency || 'N/A'}</td>
                        <td>
                          {record.immigrant_first_name && record.immigrant_last_name ? (
                            <div style={{ fontSize: '12px' }}>
                              <strong>{record.immigrant_first_name} {record.immigrant_last_name}</strong>
                              <br />
                              <span style={{ color: '#666' }}>ID: {record.linked_immigrant_id}</span>
                            </div>
                          ) : record.standalone_first_name && record.standalone_last_name ? (
                            <div style={{ fontSize: '12px' }}>
                              <strong>{record.standalone_first_name} {record.standalone_last_name}</strong>
                              <br />
                              <span style={{ color: '#999', fontStyle: 'italic' }}>Standalone</span>
                            </div>
                          ) : (
                            <span style={{ color: '#999', fontStyle: 'italic' }}>Standalone</span>
                          )}
                        </td>
                        <td>
                          <span className={`statusBadge ${record.ice_detainer_received ? 'yes' : 'no'}`}>
                            {record.ice_detainer_received ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td>
                          <div className="actionButtons">
                            <button
                              className="actionBtn viewBtn"
                              onClick={() => {
                                const recordCopy = JSON.parse(JSON.stringify(record));
                                handleViewDetentionRecord(recordCopy);
                              }}
                            >
                              View
                            </button>
                            <button
                              className="actionBtn editBtn"
                              onClick={() => {
                                const recordCopy = JSON.parse(JSON.stringify(record));
                                handleEditDetentionRecord(recordCopy);
                              }}
                              style={{
                                backgroundColor: '#2196f3',
                                color: 'white',
                                marginLeft: '5px'
                              }}
                            >
                              Edit
                            </button>
                            {/* Delete button - Admin and Security Admin only */}
                            {(isAdmin || isSecurityAdmin) && (
                              <button
                                className="actionBtn deleteBtn"
                                onClick={() => handleDeleteDetentionRecord(record)}
                                style={{
                                  backgroundColor: '#dc3545',
                                  color: 'white',
                                  marginLeft: '5px'
                                }}
                                title="Delete Detention Record"
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" style={{ textAlign: 'center' }}>
                        No records found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {detentionTotalPages > 1 && (
              <div className="pagination">
                <button
                  onClick={() => fetchDetentionRecords(detentionCurrentPage - 1)}
                  disabled={detentionCurrentPage === 1 || detentionLoading}
                  className="paginationBtn"
                >
                  Previous
                </button>
                <span>
                  Page {detentionCurrentPage} of {detentionTotalPages} ({detentionTotalCount} total records)
                </span>
                <button
                  onClick={() => fetchDetentionRecords(detentionCurrentPage + 1)}
                  disabled={detentionCurrentPage === detentionTotalPages || detentionLoading}
                  className="paginationBtn"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}

        {/* Monthly Summary Tab - Detention, Admin, and Security Admin only */}
        {activeTab === 'monthly-summary' && (isDetention || isAdmin || isSecurityAdmin) && (
          <div className="tabPanel">
            <h2>Monthly Summary</h2>
            <DetentionMonthlySummary authHeader={authHeader} />
          </div>
        )}

        {/* Add New Record Tab */}
        {activeTab === 'add-record' && !isDetention && (
          <div className="tabPanel">
            <h2>Add New Record</h2>
            <ImmigrantForm
              formData={formData}
              handleInputChange={handleInputChange}
              handleSubmit={handleSubmit}
              loading={loading}
              photoFileInputRef={photoFileInputRef}
              fieldErrors={fieldErrors}
            />
          </div>
        )}

        {/* Records Needing Approval Tab - Supervisor Only */}
        {activeTab === 'pending-approval' && isSupervisor && !isDetention && (
          <div className="tabPanel">
            <h2>Illegal Immigrant Records Needing Approval</h2>
            <div className="searchBar">
              <input
                type="text"
                placeholder="Search by name or citizenship..."
                value={searchTerm}
                onChange={handleSearch}
                className="modern-input"
              />
            </div>

            <div className="recordsTable">
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Citizenship</th>
                    <th>Contact Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {records.length > 0 ? (
                    records.map((record) => (
                        <tr key={record.id} className={record.isBeingApproved ? 'record-approved' : ''}>
                          <td>{record.id}</td>
                          <td>{`${record.firstName} ${record.lastName}`}</td>
                          <td>{record.countryOfCitizenship}</td>
                          <td>{formatDate(record.contactDate)}</td>
                          <td>
                            <button
                              className="actionBtn viewBtn"
                              onClick={() => {
                                // Create a deep copy of the record to ensure we're not sharing references
                                const recordCopy = JSON.parse(JSON.stringify(record));
                                handleViewRecord(recordCopy);
                              }}
                            >
                              View
                            </button>
                            {/* Delete button - Admin and Security Admin only */}
                            {(isAdmin || isSecurityAdmin) && (
                              <button
                                className="actionBtn deleteBtn"
                                onClick={() => handleDeleteRecord(record)}
                                title="Delete Record"
                              >
                                Delete
                              </button>
                            )}
                          </td>
                        </tr>
                      )
                    )
                  ) : (
                    <tr>
                      <td colSpan="5" style={{textAlign: 'center'}}>
                        {loading ? 'Loading records...' : 'No records found'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="pagination">
              <button
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1 || loading}
                className="paginationBtn"
              >
                Previous
              </button>
              <span>
                {loading ? 'Loading...' : `Page ${currentPage} of ${totalPages || 1} (${totalCount} total records)`}
              </span>
              <button
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages || totalPages === 0 || loading}
                className="paginationBtn"
              >
                Next
              </button>
            </div>
            {error && (
              <div className="errorMessage">
                {error}
              </div>
            )}
          </div>
        )}

        {/* Approved Records Tab - Supervisors and above */}
        {activeTab === 'approved-records' && (isSupervisor || isAdmin || isSecurityAdmin) && !isDetention && (
          <div className="tabPanel">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2>Approved Records</h2>
            </div>
            <div className="searchBar">
              <input
                type="text"
                placeholder="Search approved records..."
                value={approvedSearchTerm}
                onChange={(e) => {
                  setApprovedSearchTerm(e.target.value);
                  setApprovedCurrentPage(1);
                }}
                className="modern-input"
              />
            </div>

            <div className="recordsTable">
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Citizenship</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {approvedRecords.length > 0 ? (
                    approvedRecords.map((record) => (
                      <tr key={record.id} className={record.newlyApproved ? 'record-newly-approved' : ''}>
                        <td>{record.id}</td>
                        <td>{`${record.firstName} ${record.lastName}`}</td>
                        <td>{record.countryOfCitizenship}</td>
                        <td>
                          <div className="actionButtons">
                            <button
                              className="actionBtn viewBtn"
                              onClick={() => {
                                const recordCopy = JSON.parse(JSON.stringify(record));
                                handleViewRecord(recordCopy);
                              }}
                            >
                              View
                            </button>
                            {/* <button
                              className="actionBtn fdleBtn"
                              onClick={() => handleSubmitToFDLE_Fast(record.id)}
                              style={{
                                backgroundColor: record.submitted_to_fdle ? '#9E9E9E' : '#4CAF50',
                                color: 'white',
                                marginLeft: '5px',
                                fontSize: '11px',
                                cursor: record.submitted_to_fdle ? 'not-allowed' : 'pointer',
                                opacity: record.submitted_to_fdle ? 0.6 : 1
                              }}
                              title={record.submitted_to_fdle
                                ? `Already submitted to FDLE on ${record.fdle_submission_date ? new Date(record.fdle_submission_date).toLocaleDateString() : 'unknown date'}`
                                : "Submit to FDLE (Fast Mode)"
                              }
                              disabled={loading || record.submitted_to_fdle}
                            >
                              {record.submitted_to_fdle ? ' Submitted' : '⚡ Fast'}
                            </button> */}

                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" style={{textAlign: 'center'}}>
                        {loadingApproved ? 'Loading...' : 'No approved records'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="pagination">
              <button
                onClick={() => setApprovedCurrentPage(approvedCurrentPage - 1)}
                disabled={approvedCurrentPage === 1 || loadingApproved}
                className="paginationBtn"
              >
                Previous
              </button>
              <span>
                {loadingApproved ? 'Loading...' : `Page ${approvedCurrentPage} of ${approvedTotalPages || 1}`}
              </span>
              <button
                onClick={() => setApprovedCurrentPage(approvedCurrentPage + 1)}
                disabled={approvedCurrentPage === approvedTotalPages || approvedTotalPages === 0 || loadingApproved}
                className="paginationBtn"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* All Records Tab - Officer Only (temporarily allowing admins and security admins for testing) */}
        {activeTab === 'all-records' && (userRole === 'Officer' || userRole === 'Admin' || userRole === 'Security Admin') && !isDetention && (
          <div className="tabPanel">
            <h2>All Records</h2>
            <div className="searchBar">
              <input
                type="text"
                placeholder="Search by name or citizenship..."
                value={allSearchTerm}
                onChange={(e) => {
                  setAllSearchTerm(e.target.value);
                  setAllCurrentPage(1);
                }}
                className="modern-input"
              />
            </div>

            <div className="recordsTable">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Date of Birth</th>
                    <th>Citizenship</th>
                    <th>Contact Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {allRecords.length > 0 ? (
                    allRecords.map((record) => (
                      <tr key={record.id}>
                        <td>{`${record.firstName} ${record.lastName}`}</td>
                        <td>{formatDate(record.dateOfBirth)}</td>
                        <td>{record.countryOfCitizenship}</td>
                        <td>{formatDate(record.contactDate)}</td>
                        <td>
                          <div className="actionButtons">
                            <button
                              className="actionBtn viewBtn"
                              onClick={() => {
                                const recordCopy = JSON.parse(JSON.stringify(record));
                                handleViewRecord(recordCopy, 'all-records');
                              }}
                            >
                              View
                            </button>
                            {/* Delete button for admins only */}
                            {canDeleteRecords() && (
                              <button
                                className="actionBtn deleteBtn"
                                onClick={() => handleDeleteAllRecord(record.id, `${record.firstName} ${record.lastName}`)}
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" style={{textAlign: 'center'}}>
                        {loadingAll ? 'Loading records...' : 'No records found'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="pagination">
              <button
                onClick={() => setAllCurrentPage(allCurrentPage - 1)}
                disabled={allCurrentPage === 1 || loadingAll}
                className="paginationBtn"
              >
                Previous
              </button>
              <span>
                {loadingAll ? 'Loading...' : `Page ${allCurrentPage} of ${allTotalPages || 1} (${allTotalCount} total records)`}
              </span>
              <button
                onClick={() => setAllCurrentPage(allCurrentPage + 1)}
                disabled={allCurrentPage === allTotalPages || allTotalPages === 0 || loadingAll}
                className="paginationBtn"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* Audit Dashboard Tab - Security Admin Only */}
        {activeTab === 'audit-dashboard' && isSecurityAdmin && !isDetention && (
          <div className="tabPanel">
            <AuditDashboard />
          </div>
        )}

        {/* User Management Tab - Admin and Security Admin Only */}
        {activeTab === 'user-management' && (isAdmin || isSecurityAdmin) && !isDetention && (
          <div className="tabPanel">
            <UserManagement
              onNavigateToAuditDashboard={() => setActiveTab('audit-dashboard')}
            />
          </div>
        )}
      </div>

      {/* Record Details Modal */}
      <RecordDetailsModal
        record={selectedRecord}
        visible={modalVisible}
        onClose={closeModal}
        onApprove={handleApproveRecord}
        onUpdate={handleUpdateRecord}
        viewContext={viewContext}
      />

      {/* Detention Record Modal */}
      {detentionModalVisible && (
        <div className="modalOverlay">
          <div className="modalContent">
            <div className="modalHeader">
              <h2>Add Detention Record</h2>
              <button
                className="closeBtn"
                onClick={() => {
                  setDetentionModalVisible(false);
                  setSelectedImmigrantForDetention(null);
                }}
              >
                &times;
              </button>
            </div>
            <div className="modalBody">
              <DetentionForm
                onSubmit={handleCreateDetentionRecord}
                loading={false}
                linkedImmigrant={selectedImmigrantForDetention}
              />
            </div>
          </div>
        </div>
      )}

      {/* Detention Record Details Modal - Using DetentionEditModal in view mode */}
      <DetentionEditModal
        record={selectedDetentionRecord}
        visible={detentionRecordModalVisible}
        isViewMode={true}
        onClose={() => {
          setDetentionRecordModalVisible(false);
          setSelectedDetentionRecord(null);
        }}
        onUpdate={() => {}} // Not used in view mode
      />

      {/* Detention Record Edit Modal */}
      <DetentionEditModal
        record={selectedDetentionRecordForEdit}
        visible={editDetentionModalVisible}
        onClose={() => {
          setEditDetentionModalVisible(false);
          setSelectedDetentionRecordForEdit(null);
        }}
        onUpdate={handleUpdateDetentionRecord}
      />

      {/* Standalone Detention Record Modal */}
      <StandaloneDetentionModal
        visible={standaloneDetentionModalVisible}
        onClose={() => setStandaloneDetentionModalVisible(false)}
        onSubmit={handleCreateStandaloneDetentionRecord}
        loading={false}
      />


    </div>
  );
}
